import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import toast from 'react-hot-toast';

export interface AccountGroup {
  id: string;
  name: string;
  type: string;
  category: string;
  description: string;
  isActive: boolean;
  createdAt: string;
}

export function useAccountGroups(params: any = {}) {
  return useQuery({
    queryKey: ['accountGroups', params],
    queryFn: async () => {
      const res = await api.get('/api/accounts/groups', { params });
      return res.data;
    }
  });
}

export function useCreateAccountGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: Partial<AccountGroup>) => {
      const res = await api.post('/api/accounts/groups', data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Account group created successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create account group');
    }
  });
}

export function useUpdateAccountGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AccountGroup> }) => {
      const res = await api.put(`/api/accounts/groups/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Account group updated successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update account group');
    }
  });
}

export function useDeleteAccountGroup() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/api/accounts/groups/${id}`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Account group deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['accountGroups'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete account group');
    }
  });
}
